import React, { useState, useEffect, useContext } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	ToggleButton,
	ToggleButtonGroup,
	CircularProgress,
	Typography,
	Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import AuthContext from '../../store/auth-context';
import { getCampaignStatistics } from '../../api/email-campaign-api';

interface CampaignStatisticsDialogProps {
	campaignId: number;
	campaignName: string;
	open: boolean;
	onClose: () => void;
}

interface TimeSeriesData {
	period: string;
	sent: number;
	clicked: number;
	password_verified: number;
	code_verified: number;
}

interface StatisticsData {
	total: number;
	sent: number;
	clicked: number;
	password_verified: number;
	code_verified: number;
	time_series: TimeSeriesData[];
}

const CampaignStatisticsDialog: React.FC<CampaignStatisticsDialogProps> = ({
	campaignId,
	campaignName,
	open,
	onClose,
}) => {
	const { token } = useContext(AuthContext);
	const [groupBy, setGroupBy] = useState<'hour' | 'day'>('hour');
	const [statistics, setStatistics] = useState<StatisticsData | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (open && token) {
			loadStatistics();
		}
	}, [open, campaignId, groupBy, token]);

	const loadStatistics = async () => {
		if (!token) return;

		setIsLoading(true);
		setError(null);

		try {
			const response = await getCampaignStatistics(token, campaignId, groupBy);
			if (response.data && response.data.statistics) {
				setStatistics(response.data.statistics);
			}
		} catch (err: any) {
			console.error('Error loading statistics:', err);
			setError('Błąd podczas ładowania statystyk');
		} finally {
			setIsLoading(false);
		}
	};

	const handleGroupByChange = (event: React.MouseEvent<HTMLElement>, newValue: 'hour' | 'day' | null) => {
		if (newValue !== null) {
			setGroupBy(newValue);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth='lg'
			fullWidth
		>
			<DialogTitle>
				<Box
					display='flex'
					justifyContent='space-between'
					alignItems='center'
				>
					<Typography variant='h6'>Statystyki szczegółowe: {campaignName}</Typography>
					<Button
						onClick={onClose}
						startIcon={<CloseIcon />}
					>
						Zamknij
					</Button>
				</Box>
			</DialogTitle>

			<DialogContent>
				{error && (
					<Alert
						severity='error'
						sx={{ mb: 2 }}
					>
						{error}
					</Alert>
				)}

				{!error && (
					<>
						{/* Summary */}
						{statistics && (
							<Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
								<Typography
									variant='subtitle1'
									gutterBottom
									fontWeight='bold'
								>
									Podsumowanie
								</Typography>
								<Box
									display='flex'
									gap={3}
									flexWrap='wrap'
								>
									<Box>
										<Typography
											variant='caption'
											color='text.secondary'
										>
											Wszystkich odbiorców
										</Typography>
										<Typography variant='h6'>{statistics.total}</Typography>
									</Box>
									<Box>
										<Typography
											variant='caption'
											color='text.secondary'
										>
											Wysłano
										</Typography>
										<Typography variant='h6'>{statistics.sent}</Typography>
									</Box>
									<Box>
										<Typography
											variant='caption'
											color='text.secondary'
										>
											Kliknięto
										</Typography>
										<Typography variant='h6'>{statistics.clicked}</Typography>
									</Box>
									{statistics.password_verified > 0 && (
										<Box>
											<Typography
												variant='caption'
												color='text.secondary'
											>
												Hasło
											</Typography>
											<Typography variant='h6'>{statistics.password_verified}</Typography>
										</Box>
									)}
									{statistics.code_verified > 0 && (
										<Box>
											<Typography
												variant='caption'
												color='text.secondary'
											>
												Kod
											</Typography>
											<Typography variant='h6'>{statistics.code_verified}</Typography>
										</Box>
									)}
								</Box>
							</Box>
						)}

						{/* Group By Toggle */}
						<Box
							display='flex'
							justifyContent='space-between'
							alignItems='center'
							mb={2}
						>
							<Typography
								variant='subtitle1'
								fontWeight='bold'
							>
								Rozkład w czasie
							</Typography>
							<ToggleButtonGroup
								value={groupBy}
								exclusive
								onChange={handleGroupByChange}
								size='small'
							>
								<ToggleButton value='hour'>Godziny</ToggleButton>
								<ToggleButton value='day'>Dni</ToggleButton>
							</ToggleButtonGroup>
						</Box>

						{/* Time Series Table */}
						{isLoading ? (
							<Box
								display='flex'
								justifyContent='center'
								py={4}
							>
								<CircularProgress />
							</Box>
						) : statistics && statistics.time_series && statistics.time_series.length > 0 ? (
							<TableContainer
								component={Paper}
								sx={{ maxHeight: 400 }}
							>
								<Table
									stickyHeader
									size='small'
								>
									<TableHead>
										<TableRow>
											<TableCell>
												<strong>{groupBy === 'hour' ? 'Godzina' : 'Dzień'}</strong>
											</TableCell>
											<TableCell align='right'>
												<strong>Wysłano</strong>
											</TableCell>
											<TableCell align='right'>
												<strong>Kliknięto</strong>
											</TableCell>
											<TableCell align='right'>
												<strong>Hasło</strong>
											</TableCell>
											<TableCell align='right'>
												<strong>Kod</strong>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{statistics.time_series.map((row, index) => (
											<TableRow
												key={index}
												hover
											>
												<TableCell>{row.period}</TableCell>
												<TableCell align='right'>{row.sent}</TableCell>
												<TableCell align='right'>{row.clicked}</TableCell>
												<TableCell align='right'>{row.password_verified}</TableCell>
												<TableCell align='right'>{row.code_verified}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						) : (
							<Alert severity='info'>Brak danych do wyświetlenia</Alert>
						)}
					</>
				)}
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose}>Zamknij</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CampaignStatisticsDialog;
